import { useTheme } from '@/components/ThemeProvider';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { TradeType } from '@/page/trade/typing';
import { ArrowDown, Lightning } from '@/widget/Icon';
import { Button } from '@/components/ui/button';
import { CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { SOL_TOKEN_ADDRESS } from '@/config';
import { useInputAmount } from '@/hooks/state/useInputAmount';
import { formatDecimal, formatValueWithCompactNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { DeadlineConfig } from './config';
import { usePriceChg } from './hook/usePriceChg';
import { Loader2 } from 'lucide-react';
import { useTradeStore } from '@/page/trade/store';
import { useAppStore } from '@/store';

interface IProps {
  tradeType: TradeType;
}

export default (props: IProps) => {
  const { fwClass, chainInfo, chainCoinPrice, chainCoinBalance, setDepositOpen } = useAppStore();
  const { tokenInfo, tokenRealtimePrice, chainTokenAmount } = useTradeStore();

  const { tradeType } = props;
  const { isConnected } = useBoomWallet();
  const { theme } = useTheme();
  const { publish } = useAppEventBusContext();
  const { transactions } = useBoomWallet();
  const { t } = useTranslation();

  const {
    inputAmount: priceChg,
    setInputAmount: setPriceChg,
    onBlurInputAmount: onBlurPriceChg,
    onChangeInputAmount: onChangePriceChg,
  } = usePriceChg({ initialValue: '0', allowEmpty: true, type: tradeType });
  const {
    inputAmount: usdAmount,
    setInputAmount: setUsdAmount,
    onBlurInputAmount: onBlurUsdAmount,
    onChangeInputAmount: onChangeUsdAmount,
  } = useInputAmount('0');

  const [isCustom, setIsCustom] = useState(false);
  const [limitLoading, setLimitLoading] = useState(false);
  const isTradeBuy = tradeType == 'buy';
  const tokenPrice = tokenRealtimePrice;

  const onSelect = (percent: string) => {
    onChangePercent(percent);
    setIsCustom(false);
  };

  const onSelectCustom = () => {
    setIsCustom(true);
    onChangePercent('0');
  };

  const currentTradeInfo = useMemo(() => {
    const currentUnit = isTradeBuy ? chainInfo.symbol : tokenInfo?.symbol;
    const currentUnitBalance = isTradeBuy ? chainCoinBalance : chainTokenAmount;
    const currentUnitLogo = isTradeBuy
      ? chainInfo.icon
      : `https://dd.dexscreener.com/ds-data/tokens/solana/${tokenInfo?.address}.png?size=lg&key=8fb405`;
    return {
      currentUnit,
      currentUnitBalance,
      currentUnitLogo,
    };
  }, [isTradeBuy, chainCoinBalance, chainTokenAmount, tokenInfo, chainInfo]);

  const { inputAmount, setInputAmount, onBlurInputAmount, onChangeInputAmount } = useInputAmount();
  const [deadlineKey, setDeadlineKey] = useState<string>('never');
  const [sliderValue, setSliderValue] = useState<number[]>([0]);

  const decimals = isTradeBuy ? 9 : tokenInfo?.decimals || 1;
  const inputMint = isTradeBuy ? SOL_TOKEN_ADDRESS : tokenInfo?.address;
  const outputMint = isTradeBuy ? tokenInfo?.address : SOL_TOKEN_ADDRESS;
  const usd2Sol = useMemo(() => BigNumber(usdAmount).div(chainCoinPrice).toString(10), [usdAmount, chainCoinPrice]);

  const makingAmount = useMemo(() => {
    return BigNumber(inputAmount)
      .times(10 ** decimals)
      .decimalPlaces(0, BigNumber.ROUND_FLOOR)
      .toString();
  }, [inputAmount, decimals]);

  const currentInputAmount2Price = useMemo(() => {
    return isTradeBuy
      ? BigNumber(inputAmount || 0)
          .times(chainCoinPrice)
          .toFixed(2)
      : BigNumber(inputAmount || 0)
          .times(tokenPrice)
          .toFixed(2);
  }, [inputAmount, chainCoinPrice, tokenPrice]);

  const expiredAt = useMemo(() => {
    return deadlineKey == 'never'
      ? 'never'
      : BigNumber(Date.now() / 1000)
          .plus(deadlineKey)
          .toFixed(0);
  }, [deadlineKey]);

  const takingAmount = useMemo(() => {
    return isTradeBuy
      ? BigNumber(inputAmount || 0)
          .div(usd2Sol)
          .times(10 ** (tokenInfo?.decimals || 1))
          .decimalPlaces(0, BigNumber.ROUND_FLOOR)
          .toFixed(0)
      : BigNumber(inputAmount).times(usd2Sol).times(1e9).decimalPlaces(0, BigNumber.ROUND_FLOOR).toFixed(0);
  }, [isTradeBuy, inputAmount, usd2Sol, tokenInfo?.decimals, chainCoinPrice]);

  const isInputPriceEnough = BigNumber(currentInputAmount2Price).lt(10);
  const isInputUnitPrice = BigNumber(usdAmount).isZero();
  const isInputAmountEnough = BigNumber(inputAmount).gt(currentTradeInfo?.currentUnitBalance);
  const isBuyAmountNotEnoughGas = isTradeBuy && BigNumber(inputAmount).eq(currentTradeInfo?.currentUnitBalance);

  const limitBuy = isTradeBuy && BigNumber(priceChg).gte(100);
  const isDisabled =
    limitLoading ||
    isInputPriceEnough ||
    isInputAmountEnough ||
    isInputUnitPrice ||
    isInputAmountEnough ||
    limitBuy ||
    usdAmount == '' ||
    inputAmount == '' ||
    isBuyAmountNotEnoughGas;

  const PercentButton = useMemo(() => {
    return () =>
      ['25', '50'].map((percent) => (
        <Button
          key={percent}
          variant="secondary"
          className={cn('w-[50px] h-[32px]', fwClass, !isCustom && percent == priceChg && 'border-themeColor border-[2px]')}
          onClick={() => onSelect(percent)}
        >
          {percent}%
        </Button>
      ));
  }, [isTradeBuy, priceChg, currentTradeInfo]);

  const onSliderChange = ([value]: number[]) => {
    setSliderValue([value]);
    setInputAmount(BigNumber(currentTradeInfo?.currentUnitBalance).times(value).toString());
  };

  const onSubmit = async () => {
    if (inputMint && outputMint) {
      try {
        setLimitLoading(true);
        const res = await transactions.createLimitOrder({
          inputMint,
          outputMint,
          makingAmount,
          takingAmount,
          expiredAt,
          inputSymbol: isTradeBuy ? 'SOL' : tokenInfo?.symbol,
          outputSymbol: isTradeBuy ? tokenInfo?.symbol : 'SOL',
          inputDecimals: isTradeBuy ? 9 : tokenInfo?.decimals,
          outputDecimals: isTradeBuy ? tokenInfo?.decimals : 9,
          tokenRate: BigNumber(tokenPrice).div(chainCoinPrice).toString(10),
          solPrice: chainCoinPrice.toString(),
        });
        if (res) {
          setPriceChg('0');
          setSliderValue([0]);
          setInputAmount('');
          setUsdAmount('');
          setLimitLoading(false);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLimitLoading(false);
      }
    }
  };

  useEffect(() => {
    onChangePercent(priceChg);
  }, [priceChg]);

  const onChangePercent = (percent: string) => {
    const chg = BigNumber(percent || 0).div(100);
    const newPrice = isTradeBuy ? BigNumber(tokenPrice).times(BigNumber(1).minus(chg)) : BigNumber(tokenPrice).times(BigNumber(1).plus(chg));
    const formattedPrice = newPrice.toString(10);
    setUsdAmount(formattedPrice);
    setPriceChg(percent);
  };

  useEffect(() => {
    if (inputAmount) {
      const percent = BigNumber(inputAmount).div(currentTradeInfo?.currentUnitBalance).toString();
      setSliderValue([Number(formatDecimal(percent, 2))]);
    }
  }, [inputAmount, currentTradeInfo]);

  useEffect(() => {
    setPriceChg('0');
    setSliderValue([0]);
    setInputAmount('');
    setUsdAmount('');
  }, [isTradeBuy, tokenInfo?.address]);

  const TradeBtn = useMemo(() => {
    if (!isConnected) {
      return () => (
        <Button
          className={cn('w-full !bg-[#FCD535] rounded-[12px] h-12 text-primaryTextColor', fwClass)}
          onClick={() => publish(SubscribeEvent.Open_Wallet_Modal, true)}
        >
          {t('Trade.login')}
        </Button>
      );
    } else {
      if (isInputAmountEnough) {
        return () => (
          <Button className={cn('w-full !bg-[#FCD535] rounded-[12px] h-12 text-primaryTextColor', fwClass)} onClick={() => setDepositOpen(true)}>
            {t('Trade.insufficientBalance')}
          </Button>
        );
      }
      if (isBuyAmountNotEnoughGas && isTradeBuy) {
        return () => (
          <Button className={cn('w-full !bg-[#FCD535] rounded-[12px] h-12 text-primaryTextColor', fwClass)} disabled={isBuyAmountNotEnoughGas}>
            {t('Trade.insufficientBalanceNoGas')}
          </Button>
        );
      }
    }
    return () => (
      <Button className={cn('w-full !bg-themeColor rounded-[12px] h-12 text-primaryTextColor', fwClass)} disabled={isDisabled} onClick={onSubmit}>
        {limitLoading ? <Loader2 className="animate-spin" /> : !isInputAmountEnough && <Lightning />}
        {t(isTradeBuy ? 'Trade.buyIn' : 'Trade.sellOut')}
      </Button>
    );
  }, [isConnected, isInputAmountEnough, limitLoading, isTradeBuy, isDisabled, fwClass, isBuyAmountNotEnoughGas, onSubmit]);

  return (
    <div className="mb-[30px] flex flex-col gap-y-[16px]">
      <div className="flex justify-between">
        <div className="flex items-center" onClick={() => publish(SubscribeEvent.Open_Order_Type_Modal, true)}>
          <span className={cn('text-[16px]', fwClass)}>{t('Trade.limit')}</span>
          <ArrowDown theme={theme} className="w-[24px] h-[24px]" />
        </div>
      </div>
      <div className="flex flex-col gap-y-[20px]">
        <div>
          <div className="mb-[10px]">
            <p className="text-secondaryTextColor text-[14px] mb-[6px]">{t('Trade.priceInUSD')}</p>
            <div className="bg-[#F4F4F5CC] dark:bg-borderColor border-[1px] border-borderColor px-[12px] h-[43px] py-[8px] rounded-[12px]">
              <Input
                className="border-none outline-none shadow-none h-full p-0 text-[14px] font-[400]"
                placeholder="0.1"
                value={usdAmount}
                inputMode="decimal"
                onChange={(e) => onChangeUsdAmount(e, 18)}
                onBlur={onBlurUsdAmount}
              />
            </div>
          </div>
          <div className="flex justify-between items-center gap-x-[8px]">
            <span className="flex-shrink-0 text-[14px] font-[400]">{isTradeBuy ? t('Trade.priceDrop') : t('Trade.priceIncrease')}</span>
            <div className="flex gap-x-[8px]">
              <PercentButton />
              <div
                className={cn(
                  'rounded-[8px] h-[32px] w-[84px] flex items-center',
                  isCustom ? 'border-themeColor border-[2px]' : 'border-borderColor border-[1px]',
                )}
                onClick={onSelectCustom}
              >
                <Input
                  placeholder="Custom"
                  className="pl-[10px] w-full pr-[20px] h-full border-none outline-none shadow-none placeholder:text-[14px] font-[400] text-[14px]"
                  value={priceChg}
                  inputMode="decimal"
                  onChange={(e) => onChangePriceChg(e)}
                  onBlur={onBlurPriceChg}
                />
                <span className="pr-[10px]">%</span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <p className="text-secondaryTextColor text-[14px] mb-[6px]">{t('Trade.amount')}</p>
          <div className="bg-[#F4F4F5CC] dark:bg-borderColor border-[1px] border-borderColor px-[12px] py-[8px] rounded-[12px]">
            <CardContent className="p-0">
              <div className="flex items-center gap-x-[12px]">
                <Input
                  className="w-[80%] p-0 rounded-[8px] text-[14px] h-[36px] outline-none shadow-none border-none bg-[unset] dark:bg-[unset]"
                  placeholder="0.1"
                  inputMode="decimal"
                  value={inputAmount}
                  onChange={onChangeInputAmount}
                  onBlur={onBlurInputAmount}
                />
                <div className="flex items-center gap-x-[6px]">
                  <img src={currentTradeInfo?.currentUnitLogo} className="w-[16px] h-[16px] rounded-full" />
                  <span className={cn('text-[14px]', fwClass)}>{currentTradeInfo?.currentUnit}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="p-0">
              <div className="w-full flex justify-between text-secondaryTextColor text-[12px]">
                <span>~$ {formatValueWithCompactNumber(currentInputAmount2Price)}</span>
                <p>
                  {t('Trade.balance')} : <span className={cn('text-primaryTextColor', fwClass)}>{currentTradeInfo?.currentUnitBalance}&nbsp;</span>
                  {currentTradeInfo?.currentUnit}
                </p>
              </div>
            </CardFooter>
          </div>
        </div>
        <div className="w-full flex flex-col gap-y-[8px] mt-[6px]">
          <Slider
            trackWrapperClass="h-[4px]"
            rangeWrapperClass="bg-[#18181b80] dark:bg-[#fafafa80]"
            roundWrapperClass="border-[2px] !border-themeColor"
            max={1}
            min={0}
            step={0.01}
            value={sliderValue}
            onValueChange={onSliderChange}
          />
          <div className="flex justify-between text-[12px] text-secondaryTextColor">
            {[0, 25, 50, 75, 100].map((item) => {
              return <span key={item}>{item}%</span>;
            })}
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[14px] text-secondaryTextColor">{t('Trade.expiry')}</span>
          <Select defaultValue="0" value={deadlineKey} onValueChange={setDeadlineKey}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DeadlineConfig.map((item) => {
                return (
                  <SelectItem key={item.value} value={item.value.toString()}>
                    {t(item.label)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>
      <TradeBtn />
    </div>
  );
};
